import { useSelector } from "react-redux";
import { selectEventsData } from "../../store/events";
import { useEffect, useState } from "react";
import { sendAnalyticsEvents } from "../../analytics/common";
import { EVENTS } from "../../analytics/events";
import { AppButton, AppHeadline } from "../../components";
import styled from "styled-components";
import { usePreloadImages } from "../../hooks/usePreloadImages";
import useImagesSrc from "../../hooks/useImagesScr";

const isArabic = navigator.language.startsWith("ar");
const textAlign = isArabic ? "right" : "left";

export const ActiveIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.2955 7.60598L9.72749 13.606C9.53949 13.853 9.24849 13.999 8.93849 14.001H8.93149C8.62449 14.001 8.33449 13.859 8.14449 13.617L5.71249 10.51C5.37249 10.076 5.44849 9.44698 5.88349 9.10698C6.31749 8.76598 6.94749 8.84198 7.28749 9.27798L8.92049 11.364L12.7045 6.39498C13.0385 5.95598 13.6655 5.86998 14.1065 6.20498C14.5455 6.53998 14.6305 7.16698 14.2955 7.60598ZM10.0005 0.000976562C4.47749 0.000976562 0.000488281 4.47798 0.000488281 10.001C0.000488281 15.523 4.47749 20.001 10.0005 20.001C15.5235 20.001 20.0005 15.523 20.0005 10.001C20.0005 4.47798 15.5235 0.000976562 10.0005 0.000976562Z"
      fill="#3393EC"
    />
  </svg>
);

export const InactiveIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.2955 7.60598L9.72749 13.606C9.53949 13.853 9.24849 13.999 8.93849 14.001H8.93149C8.62449 14.001 8.33449 13.859 8.14449 13.617L5.71249 10.51C5.37249 10.076 5.44849 9.44698 5.88349 9.10698C6.31749 8.76598 6.94749 8.84198 7.28749 9.27798L8.92049 11.364L12.7045 6.39498C13.0385 5.95598 13.6655 5.86998 14.1065 6.20498C14.5455 6.53998 14.6305 7.16698 14.2955 7.60598ZM10.0005 0.000976562C4.47749 0.000976562 0.000488281 4.47798 0.000488281 10.001C0.000488281 15.523 4.47749 20.001 10.0005 20.001C15.5235 20.001 20.0005 15.523 20.0005 10.001C20.0005 4.47798 15.5235 0.000976562 10.0005 0.000976562Z"
      fill="#CCDDF0"
    />
  </svg>
);

export const Wrapper = styled.div`
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
  transition: border-radius 0.3s ease;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: 343px;
  overflow-y: scroll;
  position: relative;
  margin-bottom: ${({ isMultiple }) => isMultiple && "20px"};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: none;

  @media screen and (min-width: 450px) {
    margin-top: 20px;
  }
`;

export const ContentWrapper = styled.div`
  border-radius: 24px;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
  padding: 16px 8px 8px 8px;
`;

export const StyledList = styled.ul`
  list-style: none;
  width: 100%;
`;

export const StyledItem = styled.li`
  width: 100%;
  min-height: 96px;
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  align-items: center;
  border: ${({ active, theme }) =>
    active
      ? `1px solid ${theme.colors.colorActiveOptionBorder}`
      : `1px solid ${theme.colors.colorWhite}`};
  background-color: ${({ active, theme }) =>
    active
      ? theme.colors.colorActiveOptionBg
      : theme.colors.colorInactiveOptionBg};
  padding: 16px;
  border-radius: 16px;
  position: relative;
  cursor: pointer;
  > img {
    height: 64px;
    width: 63px;
  }
  > svg {
    margin-right: 2px;
    min-width: 20px;
  }
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

export const StyledItemTitle = styled.div`
  font-weight: ${({ theme, active }) =>
    active ? theme.fontWeights.medium : theme.fontWeights.light};
  font-size: ${(props) => props.theme.fontSizes.font18};
  line-height: ${(props) => props.theme.lineHeights.lineHeight21};
  color: ${(props) => props.theme.colors.colorText};
  text-align: ${textAlign};
  white-space: pre-line;
  padding: 0 16px;
  ${({ multiple }) => !multiple && (
    isArabic ? 'padding-left: 0;' : 'padding-right: 0;'
  )}
  width: 100%;
  flex-grow: 1;
  max-width: 177px;
`;

const TrackContainer = ({
  nextPage,
  pageEvent,
  title,
  options,
  multiple = false,
  onContinueClick,
  onSingleClick,
}) => {
  const eventsData = useSelector(selectEventsData);
  const [initEventSent, setInitEventStatus] = useState(false);
  const [chosenOptions, setChosenOptions] = useState([]);
  const { imagesSources } = useImagesSrc();

  usePreloadImages(imagesSources[`${nextPage === 'explore-solutions' ? 'explore-solutions' : nextPage}`])

  useEffect(() => {
    if (initEventSent) return;
    sendAnalyticsEvents(EVENTS[pageEvent], eventsData);
    setInitEventStatus(true)
  }, [eventsData, initEventSent, pageEvent]);

  const renderOption = (item, i) => {
    const isActive = chosenOptions.includes(item.value);
    const handleClick = () => {
      if (isActive) {
        setChosenOptions(chosenOptions.filter((value) => value !== item.value));
      } else {
        if (multiple) {
          setChosenOptions([...chosenOptions, item.value]);
        } else {
          setChosenOptions(item.value);
          onSingleClick(item.value);
        }
      }
    };
    return (
      <StyledItem
        active={isActive}
        key={item.id}
        onClick={handleClick}
        id={`option-${i + 1}`}
      >
        <img src={item.imgPath} alt={item.name} />
        <StyledItemTitle active={isActive} multiple={multiple}>{item.name}</StyledItemTitle>
        {multiple && (isActive ? ActiveIcon : InactiveIcon)}
      </StyledItem>
    );
  };

  return (
    <>
      <Wrapper isMultiple={multiple} id="scrollable-div">
        <ContentWrapper>
          <AppHeadline customStyles={`text-align: ${textAlign}; margin-${textAlign}: 8px; word-break: break-word;`}>
            {title}
          </AppHeadline>
          <StyledList>{options.map(renderOption)}</StyledList>
        </ContentWrapper>
      </Wrapper>
      {multiple && (
        <AppButton
          onClick={() => onContinueClick(chosenOptions)}
          bottomPosition={"16px"}
          disabled={!chosenOptions.length}
        />
      )}
    </>
  );
};

export default TrackContainer;
